/**
 * The `?ask=` deep-link param (Sam, 29 Sep 2026) — lets a host app (Gulf
 * Western's MAXX field-sales app is the first) hand the Genie a question
 * already typed, so a rep tapping a suggested question in MAXX lands on the
 * answer instead of the "How can I help?" start screen.
 *
 * MAXX may send up to ~40 pasted competitor product lines with embedded
 * newlines, so this is plain text, never HTML, capped defensively before it
 * is ever sent as a user message.
 */

/** Anything longer is truncated before use — MAXX's own contract cap. */
export const ASK_PARAM_MAX_LENGTH = 4000;

/**
 * Normalizes the raw `ask` query value: truncates to the max length first
 * (so a hostile/oversized value never reaches trim()+sessionStorage with its
 * full size), then trims surrounding whitespace. Returns null for an absent
 * or blank value so callers can treat it exactly like a missing param.
 */
export const parseAskParam = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const truncated = raw.slice(0, ASK_PARAM_MAX_LENGTH);
  const trimmed = truncated.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * sessionStorage key for "this exact ask text was already auto-sent for this
 * Genie in this tab". Keyed by url_name + the ask text itself (not a hash —
 * sessionStorage has no practical size pressure here and this keeps the
 * guard trivially inspectable). A reload of the same link must not re-send;
 * a different `ask` value (or a different Genie) is a new question.
 */
const askSentStorageKey = (urlName: string, ask: string) => `genie-ask-sent:${urlName}:${ask}`;

/** True if this exact (urlName, ask) pair already auto-sent in this tab/session. */
export const hasAskAlreadyBeenSent = (urlName: string, ask: string): boolean => {
  try {
    return sessionStorage.getItem(askSentStorageKey(urlName, ask)) === "1";
  } catch {
    // Storage unavailable (privacy mode, quota) — worst case the question
    // re-sends on a reload rather than never sending at all.
    return false;
  }
};

/** Marks this exact (urlName, ask) pair as sent, guarding a later reload. */
export const markAskAsSent = (urlName: string, ask: string): void => {
  try {
    sessionStorage.setItem(askSentStorageKey(urlName, ask), "1");
  } catch {
    /* storage unavailable — the guard simply doesn't persist across reload */
  }
};
